import { dataControllerApiService, type BackendDatasetResult, type BackendSubjectExport } from '../../dataControllerApi';
import type { ExportSource } from '../../../types/dataController';
import { autoSection, humanize, type ExportContext } from '../helpers';

/**
 * Staff database — one `ExportSource` per D1 / KV / Analytics Engine dataset the backend returns.
 * The backend already describes every dataset (title, description, category, subject vs actor),
 * so this only shapes rows for print.
 */

const SYSTEM_BY_TABLE = (d: BackendDatasetResult): string => {
  if (d.id === 'votes.analytics') return 'Vote analytics';
  if (d.id === 'kv.caches') return 'Cache copies';
  return `Staff database · ${d.table}`;
};

const toSource = (d: BackendDatasetResult, fetchedAt: number): ExportSource => {
  const title = d.role === 'actor' ? `${d.title} (as staff)` : d.title;
  const notes: string[] = [];
  if (d.truncated) notes.push(`${d.total.toLocaleString('en-AU')} rows exist; the ${d.rows.length.toLocaleString('en-AU')} most recent are included.`);
  if (d.matched_on.length && d.status === 'ok') notes.push(`Matched on: ${d.matched_on.join(', ')}.`);
  if (d.role === 'actor') notes.push('These rows record actions the player took as a staff member.');
  const base = {
    id: `backend.${d.id}`,
    title,
    system: SYSTEM_BY_TABLE(d),
    description: d.description,
    category: d.category,
    recordCount: d.rows.length,
    notes: notes.length ? notes : undefined,
    fetchedAt,
    durationMs: d.duration_ms,
  };
  if (d.status === 'error' || d.status === 'skipped') return { ...base, status: d.status, error: d.error, sections: [] };
  if (d.rows.length === 0) return { ...base, status: 'empty', sections: [] };
  return {
    ...base,
    status: 'ok',
    sections: [autoSection(humanize(d.table), d.rows, { total: d.truncated ? d.total : undefined, formats: d.table === 'bans' ? { created: 'datetime-s', expires: 'datetime-s', enforced: 'bool' } : undefined })],
  };
};

export interface BackendRun {
  sources: ExportSource[];
  raw: BackendSubjectExport | null;
  error: string | null;
}

/** Runs the backend export once; every dataset becomes its own source so the coverage table lists each table. */
export async function runBackendExport(ctx: ExportContext): Promise<BackendRun> {
  const fetchedAt = Date.now();
  try {
    const raw = await dataControllerApiService.exportSubject(ctx.uuid, {
      discord_id: ctx.subject.discordId,
      email: ctx.subject.email,
      username: ctx.subject.username,
      tesseract_id: ctx.subject.tesseractId,
    });
    return { sources: raw.datasets.map((d) => toSource(d, fetchedAt)), raw, error: null };
  } catch (e) {
    return { sources: [], raw: null, error: e instanceof Error ? e.message : String(e) };
  }
}
